import { useCallback, useEffect, useRef, useState } from 'react';
import { outboundAPI, repPhoneAPI } from '../services/api.js';

// ── THE REP PHONE ────────────────────────────────────────────────────────────
//
// A human sales rep talks to prospects from this browser. Their microphone is
// published into the LiveKit room the call is running in, and the AI goes silent for
// as long as they are audible (the panel yields on any participant whose identity
// starts `rep-`). There is no softphone, no SIP registration and no mobile involved —
// the browser IS the handset.
//
// ⛔ THE MICROPHONE IS ACQUIRED BEFORE THE CALL IS PLACED, AND THAT ORDER IS THE
// WHOLE DESIGN. The obvious shape — dial, then ask for the mic while it rings — puts
// a browser permission prompt in front of a rep whose prospect is already answering.
// If they fumble it, a REAL call is live to a REAL person that the rep cannot speak
// on, and the AI opens with a cold pitch the rep believed they were about to make
// themselves. So `arm()` runs first and `dial()` refuses without it. A permission
// dialog is free before the call and unrecoverable during it.
//
// ⛔ THE SPEAKERS ARE ARMED TOO, AND FOR THE SAME REASON. Brian, 2026-09-03, after the first
// real call connected with nothing audible on the desktop: "we are arming the microphone,
// but we also need to arm the speakers as well." A browser will happily let a page
// SEND audio it will never PLAY: autoplay policy can hold inbound audio mute until a
// gesture the page never asked for, and the output can be routed to the wrong device
// (laptop speaker while the headset carries the mic). `armSpeakers()` plays a test tone
// through the chosen output on the rep's click — which is both the proof and the unlock —
// and `dial()` refuses until it has happened. A mid-call "tap to hear" fallback exists for
// the cases the browser blocks anyway (RoomEvent.AudioPlaybackStatusChanged).
//
// ⛔ AND WE DIAL BOX-FIRST (`repDialed: true`, enforced server-side in
// routes/outbound.js). On the default transport the LiveKit room is not created until
// AFTER the prospect answers, so there is nothing to join until the rep is already
// late. Box-first creates the room before the lead is dialed, which is what buys the
// rep the entire ring window to get connected.

// How long to keep asking for the room after a dial. Box-first stamps it within a
// second or two, and the prospect's phone rings for far longer than this — so a miss
// past 30s is a genuine fault (dispatch down, agent crashed), not slowness. Bounded
// because an unbounded poll on a failed dial spins until the rep closes the tab.
const ROOM_WAIT_MS = 30_000;
// Fast enough that the rep is connected long before the first ring completes; slow
// enough that a 30s wait is ~30 requests, not 300.
const ROOM_POLL_MS = 1_000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** A 0.6 s, 440 Hz tone as a WAV data URL. Generated here rather than shipped as an asset
 *  so the test never depends on a static file the CSP or the CDN might not serve. */
const TEST_TONE_URL = (() => {
  const rate = 8000, secs = 0.6, n = Math.floor(rate * secs);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const str = (o, t) => { for (let i = 0; i < t.length; i++) v.setUint8(o + i, t.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const env = Math.min(1, i / 200, (n - i) / 200); // short fade so it does not click
    v.setInt16(44 + i * 2, Math.round(Math.sin(2 * Math.PI * 440 * i / rate) * 12000 * env), true);
  }
  let bin = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return 'data:audio/wav;base64,' + btoa(bin);
})();

/**
 * `status` walks: idle → arming → ready → dialing → connecting → live → ended.
 * `error` is set alongside a status of 'idle' or 'ended' — never a state of its own,
 * so the UI always has a place to return to.
 */
export function useRepPhone() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [call, setCall] = useState(null);       // { id, to }
  const [room, setRoom] = useState(null);       // LiveKit room name, once known
  // Output side. `outputs` is what the browser will let us choose from (labels appear only
  // after the mic permission), `outputId` the rep's choice ('' = system default), and
  // `speakersArmed` flips only after a test tone has actually played through it.
  const [outputs, setOutputs] = useState([]);
  const [outputId, setOutputId] = useState('');
  const [speakersArmed, setSpeakersArmed] = useState(false);
  // The browser refused to play inbound audio (autoplay policy). Surfaced, never swallowed:
  // a connected call the rep cannot hear looks exactly like a dead line.
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  // Whether THIS browser can route a call to a chosen output. LiveKit's own answer, not a
  // raw feature probe: Safari may expose setSinkId on elements and still refuse it for the
  // room, in which case the test tone would prove a device the call never uses.
  const [outputSelectable, setOutputSelectable] = useState(false);
  // What the browser is actually receiving on the call — the evidence that separates
  // "audio arrived and was not played" from "audio never arrived". Sampled every 5 s while
  // live, shown on the page, and logged to the console for a bug report.
  const [audioDiag, setAudioDiag] = useState(null);

  // Refs, not state: these are torn down from cleanup paths that must not depend on a
  // re-render having happened, and a stale closure here leaves a live microphone open.
  const roomRef = useRef(null);
  const micRef = useRef(null);
  const audioElsRef = useRef([]);
  const cancelledRef = useRef(false);
  // Monotonic dial counter. A single cancelled-boolean cannot distinguish "the loop I just
  // cancelled" from "the loop I just started", and the poll loop only samples it once a
  // second — long enough for a redial to hand the OLD loop control of the NEW call.
  const dialSeqRef = useRef(0);
  // The chosen output, readable from joinRoom without re-creating that callback.
  const outputIdRef = useRef('');
  useEffect(() => { outputIdRef.current = outputId; }, [outputId]);

  const detachAudio = useCallback(() => {
    for (const el of audioElsRef.current) {
      try { el.remove(); } catch { /* already gone */ }
    }
    audioElsRef.current = [];
  }, []);

  /** Release EVERYTHING. Safe to call from any state, and safe to call twice. */
  const teardown = useCallback(async () => {
    cancelledRef.current = true;
    const r = roomRef.current;
    roomRef.current = null;
    if (r) {
      try { await r.disconnect(); } catch { /* nothing to disconnect */ }
    }
    // ⛔ STOP THE MIC TRACK EXPLICITLY. Disconnecting the room unpublishes it but does
    // not necessarily release the device: on several browsers the tab keeps showing a
    // recording indicator, and a rep has no way to tell a stuck indicator from a hot
    // mic on someone else's call.
    const m = micRef.current;
    micRef.current = null;
    if (m) {
      try { m.stop(); } catch { /* already stopped */ }
    }
    detachAudio();
    setPlaybackBlocked(false);
  }, [detachAudio]);

  // Unmount / navigation. Without this a rep who clicks away from the page leaves a
  // published microphone in a live prospect call with no UI attached to it.
  useEffect(() => () => { teardown(); }, [teardown]);

  // ── WHAT IS THE BROWSER ACTUALLY RECEIVING? ──────────────────────────────
  // Sampled while live. bytesReceived and audio energy come from the WebRTC receiver;
  // paused/muted/sinkId from the element the track is attached to. Bytes and energy
  // rising with nothing audible = playback or routing; zeros = media never arrived.
  useEffect(() => {
    if (status !== 'live') { setAudioDiag(null); return undefined; }
    let alive = true;
    const sample = async () => {
      const r = roomRef.current;
      if (!r || !alive) return;
      const out = { canPlayback: r.canPlaybackAudio, tracks: [] };
      for (const p of r.remoteParticipants.values()) {
        for (const pub of p.audioTrackPublications.values()) {
          const t = pub.track;
          if (!t) { out.tracks.push({ who: p.identity, subscribed: false }); continue; }
          let st = null;
          try { st = await t.getReceiverStats?.(); } catch { /* not supported */ }
          const el = t.attachedElements?.[0];
          out.tracks.push({
            who: p.identity,
            subscribed: true,
            bytes: st?.bytesReceived ?? null,
            energy: st?.totalAudioEnergy != null ? Number(st.totalAudioEnergy.toFixed(3)) : null,
            concealed: st?.concealedSamples ?? null,
            paused: el ? el.paused : null,
            muted: el ? el.muted : null,
            volume: el ? el.volume : null,
            sink: el?.sinkId ?? null
          });
        }
      }
      if (alive) { setAudioDiag(out); console.log('[rep-phone] rx', JSON.stringify(out)); }
    };
    const timer = setInterval(sample, 5_000);
    const first = setTimeout(sample, 2_000);
    return () => { alive = false; clearInterval(timer); clearTimeout(first); };
  }, [status]);

  // A live call should survive a misclick on the browser's close button.
  useEffect(() => {
    if (status !== 'live') return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [status]);

  /** Output devices the browser will name for us. Labels are empty until a media
   *  permission has been granted, which is why this runs after `arm()`. */
  const refreshOutputs = useCallback(async () => {
    try {
      const { supportsAudioOutputSelection } = await import('livekit-client');
      setOutputSelectable(Boolean(supportsAudioOutputSelection?.()));
      const devs = await navigator.mediaDevices.enumerateDevices();
      const outs = devs
        .filter((d) => d.kind === 'audiooutput')
        .map((d, i) => ({ id: d.deviceId, label: d.label || `Output ${i + 1}` }));
      setOutputs(outs);
    } catch {
      setOutputs([]);
    }
  }, []);

  // A headset plugged in after arming should appear without a reload.
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return undefined;
    const onChange = () => { if (micRef.current) refreshOutputs(); };
    md.addEventListener('devicechange', onChange);
    return () => md.removeEventListener('devicechange', onChange);
  }, [refreshOutputs]);

  /** Ask for the microphone. Separate from dialing on purpose — see the header. */
  const arm = useCallback(async () => {
    setError(null);
    if (micRef.current) { setStatus('ready'); return true; }
    setStatus('arming');
    try {
      const { createLocalAudioTrack } = await import('livekit-client');
      micRef.current = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      });
      setStatus('ready');
      refreshOutputs();
      return true;
    } catch (err) {
      setStatus('idle');
      setError(
        err?.name === 'NotAllowedError'
          ? 'Microphone blocked. Allow microphone access for this site, then try again.'
          : `Could not open the microphone: ${err?.message || err}`
      );
      return false;
    }
  }, [refreshOutputs]);

  /** Play a short tone through the chosen output. This is the rep's click, so it is the
   *  gesture that unlocks playback for the rest of the session, and hearing it is the
   *  only proof that the headset — not the laptop speaker, not nothing — carries the call. */
  const armSpeakers = useCallback(async (deviceId = outputId) => {
    setError(null);
    try {
      const el = new Audio(TEST_TONE_URL);
      el.volume = 0.5;
      // Route to the chosen device where the browser allows it (Chrome/Edge). Safari has no
      // setSinkId — the tone still plays on the system default, which is what the call
      // will use there too, so the proof holds.
      if (deviceId && typeof el.setSinkId === 'function') {
        await el.setSinkId(deviceId);
      }
      await el.play();
      await new Promise((resolve) => { el.onended = resolve; setTimeout(resolve, 1500); });
      setOutputId(deviceId || '');
      setSpeakersArmed(true);
      setPlaybackBlocked(false);
      // Already on a call (device switched mid-call): apply to the live room too.
      const r = roomRef.current;
      if (r) {
        try { await r.switchActiveDevice('audiooutput', deviceId || 'default'); } catch { /* unsupported here */ }
      }
      return true;
    } catch (err) {
      setSpeakersArmed(false);
      setError(
        err?.name === 'NotAllowedError'
          ? 'The browser would not play audio. Click Test speakers again — playback needs a click.'
          : `Could not play through that output: ${err?.message || err}`
      );
      return false;
    }
  }, [outputId]);

  /** The browser blocked inbound audio mid-call. This is the rep's click to lift it. */
  const resumeAudio = useCallback(async () => {
    const r = roomRef.current;
    if (!r) return;
    try {
      await r.startAudio();
      setPlaybackBlocked(!r.canPlaybackAudio);
    } catch {
      setPlaybackBlocked(true);
    }
  }, []);

  /**
   * Ask for a publishing token until the room exists.
   *
   * The 409s are not failures and must not be rendered as any:
   *   `no_room`     — the room has not been created yet. Normal for the first second
   *                   or two of a box-first dial. Keep asking.
   *   `call_ended`  — the call is over (nobody answered, prospect hung up). Stop.
   * A 404 means the whole feature is switched off server-side, which is a completely
   * different message to a rep than "something went wrong".
   */
  const waitForToken = useCallback(async (callId, seq) => {
    const deadline = Date.now() + ROOM_WAIT_MS;
    for (;;) {
      // Superseded by a newer dial, or torn down. Either way this loop must not act.
      if (cancelledRef.current || seq !== dialSeqRef.current) return null;
      try {
        const res = await repPhoneAPI.talkTokenForCall(callId);
        return res?.data || null;
      } catch (err) {
        const code = err?.body?.error;
        if (err?.status === 409 && code === 'no_room') {
          if (Date.now() > deadline) {
            throw new Error(
              'The call was placed but never reached the voice server, so there is ' +
              'nothing to join. Check the call record before redialing.'
            );
          }
          await sleep(ROOM_POLL_MS);
          continue;
        }
        if (err?.status === 409 && code === 'call_ended') return null;
        if (err?.status === 404) {
          throw new Error('Rep calling is not switched on for this server (REP_SOFTPHONE_ENABLED).');
        }
        if (err?.status === 403) {
          throw new Error('Your account is not allowed to speak on calls.');
        }
        throw err;
      }
    }
  }, []);

  /** Join the room and publish. Shared by the outbound and inbound paths. */
  const joinRoom = useCallback(async ({ token, url, room: roomName }, seq) => {
    const { Room, RoomEvent } = await import('livekit-client');
    const r = new Room({
      // A phone call is one continuous audio stream to one person. Neither of these
      // adaptive features helps, and both add ways for audio to be degraded or dropped.
      adaptiveStream: false,
      dynacast: false
    });
    roomRef.current = r;

    // Hear the prospect. LiveKit hands us a track; it is audible only once attached to
    // an element that is actually in the document.
    r.on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind !== 'audio') return;
      const el = track.attach();
      el.autoplay = true;
      el.style.display = 'none';
      document.body.appendChild(el);
      audioElsRef.current.push(el);
      // Play explicitly and SAY SO if refused. `autoplay` alone fails silently, which is
      // how a connected call came up inaudible on 2026-09-03.
      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => setPlaybackBlocked(true));
    });
    // The browser's own verdict on whether inbound audio may play. Fires when autoplay
    // policy blocks it and again when a gesture lifts the block.
    r.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      setPlaybackBlocked(!r.canPlaybackAudio);
    });
    r.on(RoomEvent.TrackUnsubscribed, (track) => {
      try { track.detach().forEach((el) => el.remove()); } catch { /* already gone */ }
    });
    // A subscriber-side failure mid-call shows up here, not as an error. Logged so a
    // "connected but silent" report has a timeline.
    r.on(RoomEvent.Reconnecting, () => console.warn('[rep-phone] reconnecting'));
    r.on(RoomEvent.Reconnected, () => console.warn('[rep-phone] reconnected'));
    r.on(RoomEvent.Disconnected, () => {
      // The far end went away, or the server closed the room. Reflect it rather than
      // leaving a dead "live" indicator the rep will keep talking into.
      roomRef.current = null;
      detachAudio();
      setStatus((s) => (s === 'live' || s === 'connecting' ? 'ended' : s));
    });

    await r.connect(url, token);

    // ⛔ RE-CHECK AFTER CONNECT, AND DISCONNECT IF WE LOST THE RACE. `roomRef.current = r`
    // is set BEFORE this await, so a teardown landing mid-connect disconnects a room that
    // has not connected yet (a no-op) and nulls the ref — then this connect succeeds and
    // NOTHING owns the result. That leaves a browser tab silently subscribed to a live
    // prospect's audio with no UI attached to it. The mic is stopped by teardown, so it is
    // a connection leak rather than a hot mic, but it is still an open ear on a real call.
    if (seq !== undefined && seq !== dialSeqRef.current) {
      try { await r.disconnect(); } catch { /* nothing to disconnect */ }
      return;
    }
    if (roomRef.current !== r || !micRef.current) {
      try { await r.disconnect(); } catch { /* nothing to disconnect */ }
      return;
    }

    // Browser autoplay policy can leave inbound audio muted until a gesture. The test tone
    // in armSpeakers() is that gesture; this is belt and braces — and a refusal is now
    // SURFACED (playbackBlocked → "tap to hear"), never swallowed.
    try {
      await r.startAudio();
      setPlaybackBlocked(!r.canPlaybackAudio);
    } catch {
      setPlaybackBlocked(true);
    }
    // Route the call to the output the rep proved with the test tone.
    if (outputIdRef.current) {
      try { await r.switchActiveDevice('audiooutput', outputIdRef.current); } catch { /* Safari: system default */ }
    }

    // ⛔ PUBLISH LAST. The panel yields on the TRACK APPEARING, not on the join — a rep
    // who is connected but not yet publishing has not taken the call, and silencing the
    // AI at that moment would leave the prospect in dead air.
    await r.localParticipant.publishTrack(micRef.current);

    setRoom(roomName || r.name || null);
    setStatus('live');
  }, [detachAudio]);

  /** Place a call to a prospect and take it, as the human. */
  const dial = useCallback(async (to, { campaignId, fromNumberId, afterHoursOverride } = {}) => {
    setError(null);
    if (!micRef.current) {
      setError('Turn the microphone on before dialing.');
      return;
    }
    if (!speakersArmed) {
      setError('Test the speakers before dialing — you must be able to hear the prospect.');
      return;
    }
    // ⛔ CLAIM THIS DIAL. `cancelledRef` alone was a single boolean, which is not enough:
    // the poll loop below checks it at most once a second, so a rep who hangs up and
    // redials inside that window resurrects the PREVIOUS loop — which can then win a token
    // for the old call and publish the one microphone track into the wrong room. An epoch
    // makes "is this still the current dial?" answerable at every await point.
    const seq = ++dialSeqRef.current;
    cancelledRef.current = false;
    setStatus('dialing');
    setRoom(null);
    let placed;
    try {
      placed = await outboundAPI.place({
        to,
        // ⛔ The server routes on this: box-first transport + the Call-row stamp that
        // tells the panel a rep hanging up ENDS the call rather than handing the
        // prospect back to the AI.
        repDialed: true,
        // ⛔ WITHOUT THIS THE PROSPECT MAY NOT SEE THE LINE THE REP PICKED. With no
        // fromNumberId the server falls through to resolveBridgeTarget's OLDEST-ACTIVE-ROW
        // fallback — a documented trap on this platform ("row age is not a routing policy").
        // It happens to choose 832 today only because that row predates 346; adding or
        // moving a number silently re-points both the caller ID AND the box DID the leg
        // lands on. The UI says "prospects see this number", so it has to send it.
        ...(fromNumberId ? { fromNumberId } : {}),
        // A rep sales call is a campaign leg when one is chosen — that is what earns the
        // panel's CAMPAIGN_GUARDRAILS, which are gated on campaign_id, not on is_outbound.
        ...(campaignId ? { campaignId } : {}),
        // Owner-only, server-enforced. A non-owner sending it is refused with a reason.
        ...(afterHoursOverride ? { afterHoursOverride: true } : {})
      });
    } catch (err) {
      setStatus('ready');
      // The DNC refusal is the one a rep must read literally rather than as a fault.
      setError(
        err?.body?.error === 'do_not_call'
          ? 'That number is on the do-not-call list. It cannot be dialed.'
          : err?.body?.error === 'outside_calling_hours'
            ? (err?.body?.message || 'Outside calling hours.') + (afterHoursOverride ? '' : ' Platform owners can tick "Override calling hours" for a test call.')
            : err?.message || 'The call could not be placed.'
      );
      return;
    }

    const callId = placed?.data?.id;
    setCall({ id: callId, to });
    setStatus('connecting');
    try {
      const grant = await waitForToken(callId, seq);
      if (!grant) { setStatus('ended'); return; }
      if (cancelledRef.current || seq !== dialSeqRef.current) return;
      await joinRoom(grant, seq);
    } catch (err) {
      setStatus('ended');
      setError(err?.message || 'Could not join the call.');
      await teardown();
    }
  }, [joinRoom, teardown, waitForToken, speakersArmed]);

  /**
   * Take a call that is already live on one of our LINES (inbound). Keyed by DID
   * because an inbound call has no Call row to reference while it is happening.
   */
  const answerLine = useCallback(async (did) => {
    setError(null);
    if (!micRef.current) {
      setError('Turn the microphone on before answering.');
      return;
    }
    if (!speakersArmed) {
      setError('Test the speakers before answering — you must be able to hear the caller.');
      return;
    }
    const seq = ++dialSeqRef.current;
    cancelledRef.current = false;
    setStatus('connecting');
    setCall({ id: null, to: did });
    try {
      const res = await repPhoneAPI.talkTokenForLine(did);
      await joinRoom(res?.data || {}, seq);
    } catch (err) {
      // ⛔ TEAR DOWN ON A PARTIAL JOIN. `dial`'s catch does this and this one did not: if
      // connect() succeeded and publishTrack() threw, the rep is left a VISIBLE, subscribed
      // participant in a live customer call while this UI says they are idle.
      await teardown();
      setStatus('ready');
      setError(
        err?.status === 409 ? 'Nothing is live on that line right now.'
          : err?.status === 404 ? 'Rep calling is not switched on for this server.'
            : err?.message || 'Could not join that call.'
      );
    }
  }, [joinRoom, teardown, speakersArmed]);

  /** The rep puts the phone down. */
  const hangUp = useCallback(async () => {
    // On a rep-dialed call, leaving the room IS the hangup: the panel sees the `rep-`
    // participant go and ends the leg (conversationContext.repDialed). We do not need
    // — and deliberately do not have — a separate "end call" endpoint that could
    // disagree with what the room actually did.
    await teardown();
    setStatus('ended');
  }, [teardown]);

  /** Back to a dialable state without re-asking for the microphone. */
  const reset = useCallback(() => {
    // Bump the epoch so any loop still running from the previous dial can never resume into
    // this one. `cancelledRef` is cleared for the NEXT dial; the epoch is what invalidates
    // the last one.
    dialSeqRef.current += 1;
    cancelledRef.current = false;
    setCall(null);
    setRoom(null);
    setError(null);
    setStatus(micRef.current ? 'ready' : 'idle');
  }, []);

  return {
    status, error, call, room,
    armed: !!micRef.current,
    outputs, outputId, outputSelectable, speakersArmed, playbackBlocked, audioDiag,
    arm, armSpeakers, resumeAudio, dial, answerLine, hangUp, reset
  };
}

export default useRepPhone;
